import { useCallback, useMemo, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { dbui_v1_TransformRequest } from '@antimatterhq/asl-api-ts-v1';
import { useFormikContext } from 'formik';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { DataSourcesDataPreviewDialog } from '@/components/dataSources/dataPreviewDialog/DataSourcesDataPreviewDialog';
import { SilverDataSourceSchemaValues } from '@/zod/schemas/dataSource/Silver';
import {
  mapPreTransformToTransformRequest,
  mapSilverTableToTransformRequest,
  setSilverFormValues,
} from '@/lib/datasource/';
import { useGetTablePreview } from '@/hooks/dbui/useGetTablePreview';
import { Datasource_TransformStage } from '@/types/api';
import { useGetAndPollTransform } from '@/hooks/components/useGetAndPollTransform';
import { useGetFullyQualifiedTableName } from '@/hooks/components/useGetFullyQualifiedTableName';
import { prettifyTransformErrorMessage } from '@/utils/error';

export interface SilverDataPreviewContainerProps {
  tableIndex: number;
  isModifyTable: boolean;
  disabled?: boolean;
}

export const SilverDataPreviewContainer = ({
  tableIndex,
  isModifyTable,
  disabled,
}: SilverDataPreviewContainerProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDataPreviewModalClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const { dataSource: initialDatasource } = useConfigDataSource();

  const { name: bronzeTableName = '' } = useGetFullyQualifiedTableName({
    tableName: initialDatasource.spec.bronze?.bronzeTable || '',
    stage: 'bronze',
  });

  const skipPreTransform = useMemo(() => {
    return (
      initialDatasource.spec.silver?.preTransform?.skipPreTransform || false
    );
  }, [initialDatasource]);

  // get the current pre-transform values
  const { values } = useFormikContext<SilverDataSourceSchemaValues>();

  // set the autoloader input
  const autoloaderInput = useMemo(() => {
    return {
      location: initialDatasource.spec.autoloader?.location || '',
      ...initialDatasource.spec.autoloader,
    };
  }, [initialDatasource]);

  // if we are not using autoloader, call the preview table for the "source table"
  const {
    data: bronzeTablePreview,
    isFetching: getTablePreviewIsFetching,
    error: bronzeTablePreviewError,
  } = useGetTablePreview({
    name: bronzeTableName,
    disabled: !!autoloaderInput.location,
  });

  // handle the transform operation
  const transformRequest = useMemo(() => {
    // preset recreating this request object with every form change
    if (!isDialogOpen) {
      return {
        autoloaderInput: undefined,
        input: undefined,
        transforms: [],
        usePreset: initialDatasource.spec.usePreset,
      };
    }

    const datasource = setSilverFormValues(initialDatasource, values);
    const { autoloader, silver } = datasource.spec;
    const table = isModifyTable
      ? silver?.transform?.presetOverrides?.modifyTables?.[tableIndex]
      : silver?.transform?.presetOverrides?.addTables?.[tableIndex];

    if (!table) {
      throw new Error('Table missing for silver data preview');
    }

    const transforms = [mapSilverTableToTransformRequest(table, isModifyTable)];
    if (!skipPreTransform) {
      transforms.unshift(mapPreTransformToTransformRequest(datasource));
    }

    const request: Omit<dbui_v1_TransformRequest, 'warehouse'> = {
      autoloaderInput: autoloader?.location ? autoloader : undefined,
      input: bronzeTablePreview ? bronzeTablePreview : undefined,
      transforms,
      usePreset: initialDatasource.spec.usePreset,
    };

    return request;
  }, [
    bronzeTablePreview,
    initialDatasource,
    values,
    skipPreTransform,
    isModifyTable,
    tableIndex,
    isDialogOpen,
  ]);

  const {
    stages,
    isLoading: previewTableIsLoading,
    error: previewTableResponseError,
  } = useGetAndPollTransform(transformRequest, isDialogOpen);

  const previewTable = useMemo(
    () => stages?.find((stage) => stage.transformType === 'SilverTransform'),
    [stages],
  );

  const sourceTable = useMemo(() => {
    const preTransformTable = stages?.find(
      (stage) => stage.transformType === 'SilverPreTransform',
    );

    const bronzeTable = stages?.find(
      (stage) => stage.transformType === 'Input',
    );

    return (preTransformTable ||
      bronzeTable ||
      bronzeTablePreview) as Datasource_TransformStage;
  }, [bronzeTablePreview, stages]);

  const sourceTableIsLoading = useMemo(() => {
    if (!autoloaderInput.location) {
      return getTablePreviewIsFetching;
    }

    return previewTableIsLoading;
  }, [
    getTablePreviewIsFetching,
    previewTableIsLoading,
    autoloaderInput.location,
  ]);

  const sourceTableResponseError = useMemo(() => {
    if (!autoloaderInput.location) {
      return prettifyTransformErrorMessage(bronzeTablePreviewError);
    }

    return previewTableResponseError;
  }, [
    bronzeTablePreviewError,
    previewTableResponseError,
    autoloaderInput.location,
  ]);

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="primary"
        disabled={disabled}
        leftIcon="radix-icons:external-link"
      >
        Open preview
      </Button>
      <DataSourcesDataPreviewDialog
        sourceTable={sourceTable}
        previewTable={previewTable}
        sourceTableStage={skipPreTransform ? 'Bronze' : 'SilverPreTransform'}
        previewTableStage="SilverTransform"
        isOpen={isDialogOpen}
        onClose={handleDataPreviewModalClose}
        sourceTableIsLoading={sourceTableIsLoading}
        previewTableIsLoading={previewTableIsLoading}
        sourceTableResponseError={sourceTableResponseError}
        previewTableResponseError={previewTableResponseError}
      />
    </>
  );
};
